import { Button, Input } from 'antd';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import * as Yup from 'yup';

import Loader from '../Loader';
import FileUploader from '../utils/imageUploader';

import useCreateTestimonial from '@/hooks/testimonial/useCreateTestimonial';
import useUpdateTestimonial from '@/hooks/testimonial/useEditTestimonial';
import {
	ITestimonial,
	ITestimonialDto,
} from '@/interfaces/entities/testimonal';
import { notificationService } from '@/services/notification.service';

type PropType = { editTestimonial: ITestimonial | null; setToggle: () => void };

export const CreateTestimonial = ({ editTestimonial, setToggle }: PropType) => {
	const [loading, setLoading] = useState<boolean>(false);
	const createTestimonial = useCreateTestimonial();
	const updateTestimonial = useUpdateTestimonial();

	const [initialValues, setInitialValues] = useState<ITestimonialDto>({
		name: '',
		email: '',
		mobile: '',
		address: '',
		organization: '',
		comments: '',
		img: null,
	});

	const handleSubmit = async (
		values: ITestimonialDto,
		{ resetForm }: { resetForm: () => void },
	) => {
		console.log(initialValues, 'intial');

		setLoading(true);
		if (!editTestimonial) {
			const formData = new FormData();
			formData.append('name', values.name);
			formData.append('email', values.email ?? '');
			formData.append('mobile', values.mobile ?? '');
			formData.append('address', values.address ?? '');
			formData.append('organization', values.organization ?? '');
			formData.append('comments', values.comments);

			// Handle image file
			if (values.img && values.img instanceof File) {
				formData.append('img', values.img); // actual file
			} else if (typeof values.img === 'string') {
				formData.append('img_url', values.img); // just the URL
			}
			try {
				await createTestimonial.mutateAsync(formData);
				resetForm();
				setToggle();
			} catch (error) {
				notificationService.error('Testimonial Created failed');
			} finally {
				setLoading(false);
			}
		} else {
			const formData = new FormData();
			formData.append('name', values.name);
			formData.append('email', values.email ?? '');
			formData.append('mobile', values.mobile ?? '');
			formData.append('address', values.address ?? '');
			formData.append('organization', values.organization ?? '');
			formData.append('comments', values.comments);

			// Handle image file
			if (values.img && values.img instanceof File) {
				formData.append('img', values.img); // actual file
			} else if (typeof values.img === 'string') {
				formData.append('img_url', values.img); // just the URL
			}
			if (editTestimonial.id) {
				try {
					await updateTestimonial.mutateAsync([editTestimonial.id, formData]);
					resetForm();
					setToggle();
				} catch (error) {
					notificationService.error('Testimonial Updated failed');
				} finally {
					setLoading(false);
				}
			}
		}
	};

	const handleCancel = () => {
		setToggle();
		setInitialValues({
			name: '',
			email: '',
			mobile: '',
			address: '',
			organization: '',
			comments: '',
			img: null,
		});
	};

	const blogValidationSchema = Yup.object().shape({
		name: Yup.string().required('Name is required'),
		email: Yup.string().email('Invalid email').nullable(),
		mobile: Yup.string()
			.nullable()
			.test('is-valid', 'Invalid phone number', (value) => {
				return value ? value.length >= 10 : false;
			}),
		address: Yup.string().nullable(),
		organization: Yup.string().nullable(),
		comments: Yup.string().required('Comments are required'),
		img: Yup.mixed().nullable(),
	});

	useEffect(() => {
		if (editTestimonial) {
			setInitialValues({
				name: editTestimonial.name,
				email: editTestimonial.email,
				mobile: editTestimonial.mobile,
				address: editTestimonial.address,
				organization: editTestimonial.organization,
				comments: editTestimonial.comments,
				img: editTestimonial.img,
			});
		}
	}, [editTestimonial]);

	return (
		<div className="w-[100%] h-[100%]">
			{loading && (
				<div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30 flex-col gap-5">
					<Loader />
					<p className="text-[20px] font-semibold">Processing...</p>
				</div>
			)}

			<Formik
				initialValues={initialValues}
				onSubmit={handleSubmit}
				validationSchema={blogValidationSchema}
				enableReinitialize={true}
			>
				{({
					handleChange,
					handleBlur,
					values,
					errors,
					touched,
					setFieldValue: _setFieldValue,
					setFieldTouched,
				}) => (
					<Form>
						<div className="flex justify-between gap-5 mb-5">
							<div className="w-1/2 flex flex-col gap-3">
								<label className="text-[20px] font-semibold">Name</label>
								<Input
									name="name"
									value={values.name}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Enter Name"
									className="py-[10px] h-[50px] text-[18px]"
								/>
								{touched.name && errors.name && (
									<div className="text-red-500">{errors.name}</div>
								)}
							</div>

							<div className="w-1/2 flex flex-col gap-3">
								<label className="text-[20px] font-semibold">Email</label>
								<Input
									name="email"
									value={values.email ?? ''}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Enter email"
									className="py-[10px] h-[50px] text-[18px]"
								/>
								{touched.email && errors.email && (
									<div className="text-red-500">{errors.email}</div>
								)}
							</div>
						</div>
						<div className="flex justify-between gap-5 mb-5">
							<div className=" w-1/2 flex flex-col gap-3">
								<label className="text-[20px] font-semibold">Mobile</label>
								<div className="w-full relative flex border-[1px] border-[#CED0D6] rounded-[12px] focus-within:border-gray-500 w-full">
									<PhoneInput
										name="mobile"
										defaultCountry="AU"
										value={values.mobile ?? ''}
										onChange={(value) => _setFieldValue('mobile', value)} // ✅ updates Formik value
										onBlur={() => setFieldTouched('mobile', true)}
										placeholder="Enter your phone number"
										className="p-[10px] h-[50px] text-[18px] w-full"
									/>
								</div>

								{touched.mobile && errors.mobile && (
									<div className="text-red-500">{errors.mobile}</div>
								)}
							</div>
							<div className="w-1/2 flex flex-col gap-3">
								<label className="text-[20px] font-semibold">Organization</label>
								<Input
									name="organization"
									value={values.organization ?? ''}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Enter Organization"
									className="py-[10px] h-[50px] text-[18px]"
								/>
								{touched.organization && errors.organization && (
									<div className="text-red-500">{errors.organization}</div>
								)}
							</div>
							
						</div>
						<div className="w-full flex flex-col gap-3">
								<label className="text-[20px] font-semibold">Address</label>
								<Input
									name="address"
									value={values.address ?? ''}
									onChange={handleChange}
									onBlur={handleBlur}
									placeholder="Enter address"
									className="py-[10px] h-[50px] text-[18px]"
								/>
								{touched.address && errors.address && (
									<div className="text-red-500">{errors.address}</div>
								)}
							</div>
							
						<div className="flex flex-col gap-3 mt-5">
							<label className="text-[20px] font-semibold">Comments</label>
							<Input.TextArea
								name="comments"
								value={values.comments}
								onChange={handleChange}
								onBlur={handleBlur}
								placeholder="Enter Comments"
								rows={4}
								className="py-[10px] h-[50px] text-[18px]"
							/>
							{touched.comments && errors.comments && (
								<div className="text-red-500">{errors.comments}</div>
							)}
						</div>

						{/* Image Upload */}
						<div className="flex flex-col gap-3 mt-5">
							<label className="text-[20px] font-semibold">Profile Image</label>
							<FileUploader
								mode="image-only"
								imageField="img"
								using="testimonial"
							/>
						</div>
						{touched.img && errors.img && (
							<div className="text-red-500">{errors.img}</div>
						)}

						{/* Submit Button */}
						<div className="mt-5 w-full flex justify-end pb-10 gap-5">
							<Button
								className="text-[16px] font-semibold"
								htmlType="button"
								onClick={handleCancel}
							>
								Cancel
							</Button>
							<Button
								color="default"
								variant="solid"
								className="text-[16px] font-semibold"
								htmlType="submit"
							>
								Submit
							</Button>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);
};
